import { useParams, Link } from "react-router-dom";
import { SITE_URL } from "@/config/site";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { trackUseCaseView } from "@/lib/analytics";
import { useEffect } from "react";
import PageLayout from "@/components/PageLayout";

const UseCaseDetailPage = () => {
  const { slug } = useParams();
  useEffect(() => {
    if (slug) trackUseCaseView(slug);
  }, [slug]);
  const { t } = useTranslation();
  const cases = t("useCases.list", { returnObjects: true }) || [];
  const slugs = [
    "analyse-pdf",
    "generation-images",
    "assistant-chat",
    "redaction-ia",
    "generation-code",
    "redaction-emails",
  ];
  const idx = slugs.indexOf(slug);
  const data = cases[idx] || {};

  if (!data.title)
    return (
      <PageLayout>
        <div className="text-center py-40 text-slate-400">Page introuvable</div>
      </PageLayout>
    );

  const siteUrl = SITE_URL;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: `${siteUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: t("useCases.index.title", "Cas d'usage"),
            item: `${siteUrl}/use-cases`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: data.title,
            item: `${siteUrl}/use-cases/${slug}`,
          },
        ],
      },
      {
        "@type": "Article",
        headline: data.title,
        description: data.meta || data.intro,
        url: `${siteUrl}/use-cases/${slug}`,
        author: { "@type": "Organization", name: "Stronger Bot" },
        publisher: { "@type": "Organization", name: "Stronger Bot" },
      },
    ],
  };

  return (
    <PageLayout>
      <SEO
        title={data.title}
        description={data.meta}
        canonical={`/use-cases/${slug}`}
        jsonLd={jsonLd}
      />
      <article className="max-w-3xl mx-auto px-4 py-16">
        <Link
          to="/use-cases"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> {t("useCases.backToList")}
        </Link>
        <h1 className="font-heading text-4xl font-bold text-white mb-4">
          {data.title}
        </h1>
        <p className="text-xl text-slate-400 mb-10">{data.intro}</p>

        {(data.sections || []).map((s, i) => (
          <section key={i} className="mb-10">
            <h2 className="text-2xl font-semibold text-white mb-3">
              {s.title}
            </h2>
            <p className="text-slate-400 leading-relaxed">{s.content}</p>
          </section>
        ))}

        {data.benefits && (
          <div className="glass rounded-2xl p-6 mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">
              {t("useCases.benefitsTitle")}
            </h2>
            <ul className="space-y-3">
              {data.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-300">
                  <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="glass rounded-2xl p-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-3">
            {t("useCases.ctaTitle")}
          </h2>
          <p className="text-slate-400 mb-6">{t("useCases.ctaSubtitle")}</p>
          <Link to="/register">
            <Button
              size="lg"
              className="btn-glow bg-primary-strong hover:bg-primary-strong/90"
            >
              {t("landing.cta")} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </article>
    </PageLayout>
  );
};
export default UseCaseDetailPage;
